import { useTheme } from "@mui/material";
import { useAnalytics } from "api/analytics";
import { ReactComponent as ChartComparisonLine } from "components/Icons/chartComparisonLine.svg";
import { ReactComponent as ChartLine } from "components/Icons/chartLine.svg";
import { OLTheme } from "contexts/Theme/ThemeProvider";
import { ExternalLink } from "ExternalLink";
import { useMessage } from "hooks/intl";
import { LinkItem } from "LinkItem";
import { toNumber } from "lodash";
import { useState } from "react";
import {
  AggregationType,
  ChartId,
  ChartInterval,
  ChartTab,
  GeneralOverviewData,
  GeneralOverviewMetrics,
  GeneralOverviewMetricsUnion,
  LineChartData,
} from "types/charts";
import { OLDate } from "utilities/dateFormat";
import { links } from "utilities/links";
import { LineChartOverview } from "./LineChartWithOverview/LineChartOverview";
import {
  currentDate,
  formatDate,
  formatDateToParse,
  getComparativeDate,
  getFormatByAggregationType,
  getFormattedSelectedDate,
  getStartDate,
  showComparativeData,
} from "./LineChartWithOverview/utils";

const activeIntervalSettingsInitialValues: ChartInterval = {
  intervalLabel: "last30Days",
  aggregationType: AggregationType.DAY,
  intervalStartDate: getStartDate(currentDate, 30),
  intervalEndDate: formatDate(currentDate),
};

export function GeneralOverview() {
  const { message } = useMessage();
  const theme: OLTheme = useTheme();

  const {
    REGISTERED_MEMBERS,
    ACTIVE_MEMBERS,
    REVENUE,
    AVG_SPENDING,
    TRANSACTIONS,
    AVG_NUMBER_OF_TRANSACTIONS,
    AVG_TRANSACTION_VALUE,
  } = GeneralOverviewMetrics;

  const [activeTab, setActiveTab] =
    useState<GeneralOverviewMetricsUnion>(REGISTERED_MEMBERS);
  const [activeIntervalSettings, setActiveIntervalSettings] = useState(
    activeIntervalSettingsInitialValues,
  );
  const { useGeneralOverviewAnalytics } = useAnalytics();
  const { data } = useGeneralOverviewAnalytics({
    filters: [
      {
        condition: "",
        query: "dataType",
        value: activeTab,
      },
      ...(activeIntervalSettings.aggregationType
        ? [
            {
              condition: "",
              query: "aggregationType",
              value: activeIntervalSettings.aggregationType,
            },
          ]
        : []),
      ...(activeIntervalSettings.intervalStartDate
        ? [
            {
              condition: "",
              query: "intervalStartDate",
              value: activeIntervalSettings.intervalStartDate,
            },
          ]
        : []),
      ...(activeIntervalSettings.intervalEndDate
        ? [
            {
              condition: "",
              query: "intervalEndDate",
              value: activeIntervalSettings.intervalEndDate,
            },
          ]
        : []),
    ],
  });

  const { data: comparativeData } = useGeneralOverviewAnalytics({
    filters: [
      {
        condition: "",
        query: "dataType",
        value: activeTab,
      },
      ...(activeIntervalSettings.aggregationType
        ? [
            {
              condition: "",
              query: "aggregationType",
              value: activeIntervalSettings.aggregationType,
            },
          ]
        : []),
      ...(activeIntervalSettings.intervalStartDate
        ? [
            {
              condition: "",
              query: "intervalStartDate",
              value: getComparativeDate(activeIntervalSettings, "start"),
            },
          ]
        : []),
      ...(activeIntervalSettings.intervalEndDate
        ? [
            {
              condition: "",
              query: "intervalEndDate",
              value: getComparativeDate(activeIntervalSettings, "end"),
            },
          ]
        : []),
    ],
  });

  const analyticsData: GeneralOverviewData = data;
  const analyticsComparativeData: GeneralOverviewData = showComparativeData(
    activeIntervalSettings.intervalLabel,
  )
    ? comparativeData
    : [];

  const chartTabs: ChartTab[] = [
    {
      name: REGISTERED_MEMBERS,
      tooltip: {
        externalLink: {
          path: "https://help.openloyalty.io/main-features/dashboard#registered-members",
        },
        text: message(
          { id: "registeredMembersTooltip" },
          {
            link: (
              <ExternalLink path="https://help.openloyalty.io/main-features/dashboard#registered-members">
                {message({ id: "learnMore" })}
              </ExternalLink>
            ),
          },
        ),
      },
      value: analyticsData?.header?.registeredMembers,
      formatNumber: true,
    },
    {
      name: ACTIVE_MEMBERS,
      tooltip: {
        externalLink: {
          path: "https://help.openloyalty.io/main-features/dashboard/general-overview#active-members",
        },
        internalLink: {
          text: message({
            id: "activeMemberSettings",
          }),
          to: links.membersConfiguration,
        },
        text: message(
          { id: "activeMembersTooltip" },
          {
            configuration: (
              <LinkItem blueSmall to={links.membersConfiguration}>
                {message({ id: "configuration" })}
              </LinkItem>
            ),
          },
        ),
      },
      value: analyticsData?.header?.activeMembers,
      formatNumber: true,
    },
    {
      name: REVENUE,
      tooltip: {
        externalLink: {
          path: "https://help.openloyalty.io/main-features/dashboard#revenue",
        },
        text: message(
          { id: "revenueTooltip" },
          {
            link: (
              <ExternalLink path="https://help.openloyalty.io/main-features/dashboard#revenue">
                {message({ id: "learnMore" })}
              </ExternalLink>
            ),
          },
        ),
      },
      value: analyticsData?.header?.revenue,
      formatCurrency: true,
    },
    {
      name: "avgSpendingWithCurrency",
      query: AVG_SPENDING,
      tooltip: {
        externalLink: {
          path: "https://help.openloyalty.io/main-features/dashboard#avg.-spending",
        },
        text: message(
          { id: "avgSpendingTooltip" },
          {
            link: (
              <ExternalLink path="https://help.openloyalty.io/main-features/dashboard#avg.-spending">
                {message({ id: "learnMore" })}
              </ExternalLink>
            ),
          },
        ),
      },
      value: analyticsData?.header?.avgSpending,
      formatCurrency: true,
    },
    {
      name: TRANSACTIONS,
      tooltip: {
        externalLink: {
          path: "https://help.openloyalty.io/main-features/dashboard#transactions",
        },
        text: message(
          { id: "transactionsTooltip" },
          {
            link: (
              <ExternalLink path="https://help.openloyalty.io/main-features/dashboard#transactions">
                {message({ id: "learnMore" })}
              </ExternalLink>
            ),
          },
        ),
      },
      value: analyticsData?.header?.transactions,
      formatNumber: true,
    },
    {
      name: AVG_TRANSACTION_VALUE,
      tooltip: {
        externalLink: {
          path: "https://help.openloyalty.io/main-features/dashboard#avg.-spending",
        },
        text: message(
          { id: "avgTransactionValueTooltip" },
          {
            link: (
              <ExternalLink path="https://help.openloyalty.io/main-features/dashboard#avg.-spending">
                {message({ id: "learnMore" })}
              </ExternalLink>
            ),
          },
        ),
      },
      value: analyticsData?.header?.avgTransactionValue,
      formatCurrency: true,
    },
    {
      name: AVG_NUMBER_OF_TRANSACTIONS,
      tooltip: {
        externalLink: {
          path: "https://help.openloyalty.io/main-features/dashboard#avg.-number-of-transactions",
        },
        text: message(
          { id: "avgNumberOfTransactionsTooltip" },
          {
            link: (
              <ExternalLink path="https://help.openloyalty.io/main-features/dashboard#avg.-number-of-transactions">
                {message({ id: "learnMore" })}
              </ExternalLink>
            ),
          },
        ),
      },
      value: analyticsData?.header?.avgNumberOfTransactions,
      formatCurrency: true,
    },
  ];

  const { intervalLabel, intervalStartDate, intervalEndDate } =
    activeIntervalSettings;

  const isCustomDateRangeSelected = intervalLabel === "customDateRange";
  const formattedIntervalStartDate =
    getFormattedSelectedDate(intervalStartDate);
  const formattedIntervalEndDate = getFormattedSelectedDate(intervalEndDate);

  const lineChartData: LineChartData[] = analyticsData?.data && [
    {
      color: theme.primary500,
      data: Object.keys(analyticsData.data).map((key) => {
        const { aggregationType } = analyticsData;
        const dateToParse = formatDateToParse(key, aggregationType);

        return {
          x: Date.parse(dateToParse)
            ? OLDate(dateToParse, {
                formatString: getFormatByAggregationType(aggregationType),
              })
            : "",
          y: toNumber(analyticsData.data[key]),
        };
      }),
      id: ChartId.MAIN,
      legendIcon: <ChartLine />,
      legendLabel: !isCustomDateRangeSelected
        ? message({
            id: intervalLabel,
          })
        : `${formattedIntervalStartDate} - ${formattedIntervalEndDate}`,
    },
    ...(analyticsComparativeData?.data
      ? [
          {
            color: theme.primary500,
            data: Object.keys(analyticsData.data).map((key, index) => {
              const { aggregationType } = analyticsData;
              const dateToParse = formatDateToParse(key, aggregationType);
              const comparativeDateToParse = formatDateToParse(
                Object.keys(analyticsComparativeData?.data)[index],
                aggregationType,
              );
              const yValue = toNumber(
                Object.values(analyticsComparativeData?.data)[index],
              );

              return {
                x: Date.parse(dateToParse)
                  ? OLDate(dateToParse, {
                      formatString: getFormatByAggregationType(aggregationType),
                    })
                  : "",
                y: isNaN(yValue) ? 0 : yValue,
                comparativeDate: OLDate(comparativeDateToParse, {
                  formatString: getFormatByAggregationType(aggregationType),
                }),
              };
            }),
            id: ChartId.COMPARATIVE,
            legendIcon: <ChartComparisonLine />,
            legendLabel: message({ id: "comparisonPeriod" }),
          },
        ]
      : []),
  ];

  return (
    <LineChartOverview
      chartName="generalOverview"
      tabs={chartTabs}
      data={lineChartData}
      activeTab={activeTab}
      aggregationType={analyticsData?.aggregationType}
      activeIntervalSettings={activeIntervalSettings}
      setActiveIntervalSettings={setActiveIntervalSettings}
      setActiveTab={setActiveTab}
      enableDownload
    />
  );
}
